/**
 * Bookings & Smart Scheduling — verified against cms-backend
 * src/scheduling/{dto,booking.controller,booking.service,suggestion.*}.ts at
 * main 1c40eef (api-v1.0 + ZOY-250 clinic-wide list + ZOY-251 derived
 * `delivered`).
 *
 * Booking status is ONLY booked | cancelled | no_show. `delivered` is derived
 * per read from the effective delivered session (never stored). The
 * clinic-wide list REQUIRES a bounded startsAt window (≤ 93 days), returns
 * every status unless filtered, default order startsAt asc + id tie-break,
 * cursor pagination. Every mutation is revalidated server-side; a refused
 * save answers 409 with `code` + `violations[]` (engine) or a single
 * exclusion code (database overlap arbiter). No DELETE route exists.
 */

import { apiRequest } from '../api/http';
import { buildQueryParam, type PagedResult, type QuerySpecInput } from '../api/querySpec';

/** ZOY-237: the delivery mode — in clinic (optional room), at home (address + travel), online (a join destination). */
export const LOCATION_TYPES = ['in_clinic', 'off_site', 'online'] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];
export const isLocationType = (v: unknown): v is LocationType =>
  (LOCATION_TYPES as readonly unknown[]).includes(v);

/** ZOY-684: where an ONLINE session is joined — a meeting link on another product, or Zoyaar-hosted video. */
export const JOIN_KINDS = ['external_url', 'zoyaar_hosted'] as const;
export type JoinKind = (typeof JOIN_KINDS)[number];
export const MAX_JOIN_URL_LENGTH = 2048;

/** The backend's rule for an external link: absolute https, a host, no credentials, bounded. */
export function isValidExternalJoinUrl(url: string): boolean {
  if (url.length === 0 || url.length > MAX_JOIN_URL_LENGTH) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return (
    parsed.protocol === 'https:' &&
    parsed.hostname.length > 0 &&
    parsed.username === '' &&
    parsed.password === ''
  );
}

/**
 * Product 2026-09-16 — the T−15 lock: from 15 minutes before its current
 * start a Zoyaar-hosted session (its event has gone live) can no longer be
 * moved or cancelled through the Clinic's flows. No-show, start and
 * delivery stay open. An external-link session is never locked.
 */
export const HOSTED_SESSION_LEAD_MS = 15 * 60_000;
export const hostedSessionLocked = (
  joinKind: string | null | undefined,
  startsAt: string,
  now: number = Date.now(),
): boolean => joinKind === 'zoyaar_hosted' && now >= Date.parse(startsAt) - HOSTED_SESSION_LEAD_MS;
export const BOOKING_STATUSES = ['booked', 'cancelled', 'no_show', 'incomplete'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/**
 * ZOY-546: the ONE lifecycle answer a session surface renders — derived
 * server-side on every read, never stored, never recomputed on the client
 * from status + delivered.
 */
export const EXECUTION_STATUSES = [
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
  'no_show',
  'incomplete',
] as const;
export type ExecutionStatus = (typeof EXECUTION_STATUSES)[number];

export interface BookingView {
  id: string;
  patientId: string;
  serviceId: string;
  practitionerUid: string;
  locationType: LocationType | (string & {});
  roomId: string | null;
  /** The repeat run this booking was created in (2026-10-28); null when booked alone. */
  seriesId?: string | null;
  packageItemId: string | null;
  startsAt: string;
  endsAt: string;
  visitAddress: string | null;
  travelAllowanceMinutes: number;
  status: BookingStatus;
  statusReason: string | null;
  /** Product 2026-09-06: the optional reason given when this booking went beyond its package plan. */
  overScheduleReason?: string | null;
  /** ZOY-546 start facts — set once by the start endpoint; null = not started. */
  startedAt: string | null;
  startedBy: string | null;
  /** ZOY-684: the join destination of an online session; null on every other mode. */
  joinKind?: JoinKind | null;
  joinUrl?: string | null;
  /** ZOY-685: the group occurrence this booking is a participation of; null = a single-patient booking. */
  groupSessionId?: string | null;
  createdBy: string;
  createdAt: string;
  /** ZOY-543: the declared clinical path rides the service join; null = unclassified legacy. */
  service: { name: string; workflowType: string | null };
  /**
   * ZOY-408: minimal display identity so booking surfaces render a row
   * without one Patient request per row — a name for display, never the
   * patient record. patientId stays the stable identifier.
   */
  patient: {
    firstName: string;
    lastName: string;
    /** ZOY-546: false = Guest — bookable, but clinical work may not start. */
    registered: boolean;
  };
  /** ZOY-251: true only while an EFFECTIVE delivered session anchors here. */
  delivered: boolean;
  /** ZOY-546: the derived session lifecycle — render this, never derive it. */
  executionStatus: ExecutionStatus;
  /** ZOY-250 list only: the frozen occupied window (buffers + travel). */
  occupiedFrom?: string | null;
  occupiedUntil?: string | null;
}

export const BookingErrorCode = {
  NotFound: 'BOOKING_NOT_FOUND',
  NotActive: 'BOOKING_NOT_ACTIVE',
  PractitionerNotFound: 'BOOKING_PRACTITIONER_NOT_FOUND',
  PractitionerNotActiveMember: 'BOOKING_PRACTITIONER_NOT_ACTIVE_MEMBER',
  RoomNotFound: 'BOOKING_ROOM_NOT_FOUND',
  ServiceNotFound: 'BOOKING_SERVICE_NOT_FOUND',
  PackageItemMismatch: 'BOOKING_PACKAGE_ITEM_MISMATCH',
  InvalidRange: 'BOOKING_INVALID_RANGE',
  AlreadyDelivered: 'BOOKING_ALREADY_DELIVERED',
  /** ZOY-546: the start act is one-shot. */
  AlreadyStarted: 'BOOKING_ALREADY_STARTED',
  /** ZOY-547: only a STARTED session can close incomplete. */
  NotStarted: 'BOOKING_NOT_STARTED',
  PatientOverlap: 'BOOKING_PATIENT_OVERLAP',
  PractitionerOverlap: 'BOOKING_PRACTITIONER_OVERLAP',
  RoomOverlap: 'BOOKING_ROOM_OVERLAP',
  QueryRangeRequired: 'BOOKING_QUERY_RANGE_REQUIRED',
  QueryRangeTooWide: 'BOOKING_QUERY_RANGE_TOO_WIDE',
  // Engine violations (wire codes), any number of which ride `violations[]`.
  ServiceNotBookable: 'BOOKING_SERVICE_NOT_BOOKABLE',
  ServiceLocationNotEnabled: 'BOOKING_SERVICE_LOCATION_NOT_ENABLED',
  PractitionerNotCapable: 'BOOKING_PRACTITIONER_NOT_CAPABLE',
  OutsideWorkingHours: 'BOOKING_OUTSIDE_WORKING_HOURS',
  PractitionerTimeOff: 'BOOKING_PRACTITIONER_TIME_OFF',
  DailyCapExceeded: 'BOOKING_DAILY_CAP_EXCEEDED',
  WeeklyCapExceeded: 'BOOKING_WEEKLY_CAP_EXCEEDED',
  RoomDisabled: 'BOOKING_ROOM_DISABLED',
  RoomNotCapable: 'BOOKING_ROOM_NOT_CAPABLE',
  RoomOutsideWorkingHours: 'BOOKING_ROOM_OUTSIDE_WORKING_HOURS',
  RoomBlockout: 'BOOKING_ROOM_BLOCKOUT',
  // ZOY-237 / ZOY-684 / ZOY-685 / T−15 lock (all typed 400 / 409).
  RoomNotAllowedOnline: 'BOOKING_ROOM_NOT_ALLOWED_ONLINE',
  JoinDestinationRequiresOnline: 'BOOKING_JOIN_DESTINATION_REQUIRES_ONLINE',
  JoinUrlInvalid: 'BOOKING_JOIN_URL_INVALID',
  JoinUrlNotAllowed: 'BOOKING_JOIN_URL_NOT_ALLOWED',
  GroupParticipationFollowsOccurrence: 'BOOKING_GROUP_PARTICIPATION_FOLLOWS_OCCURRENCE',
  OnlineSessionLocked: 'BOOKING_ONLINE_SESSION_LOCKED',
} as const;

export const BOOKING_QUERY_MAX_RANGE_DAYS = 93;

export interface CreateBookingBody {
  serviceId: string;
  practitionerUid: string;
  locationType: LocationType;
  roomId?: string;
  /** One client-made UUID per repeat run, the same on every date of it. */
  seriesId?: string;
  packageItemId?: string;
  startsAt: string;
  endsAt: string;
  visitAddress?: string;
  travelAllowanceMinutes?: number;
  /** Optional, stored on the booking; offered when the chosen line is already beyond its plan. */
  overScheduleReason?: string;
  /** ZOY-684: online only — the destination (a link for external_url). */
  joinKind?: JoinKind;
  joinUrl?: string;
}

export interface RescheduleBookingBody {
  startsAt?: string;
  endsAt?: string;
  practitionerUid?: string;
  /** Explicit null clears the room; undefined keeps it. */
  roomId?: string | null;
  locationType?: LocationType;
  visitAddress?: string;
  travelAllowanceMinutes?: number;
  /** ZOY-684 tri-state: omitted keeps, null clears (leaving online clears silently server-side). */
  joinKind?: JoinKind | null;
  joinUrl?: string | null;
}

/** GET /v1/bookings — the Calendar data source (ZOY-250). */
/**
 * Information riding a COMMITTED booking (Product 2026-09-06): the booking
 * exists; nothing here is a failure and nothing is retried.
 */
export interface OverScheduleWarning {
  code: 'PACKAGE_OVER_SCHEDULE';
  packageItemId: string;
  plannedSessions: number;
  deliveredSessions: number;
  scheduledSessions: number;
  /** Including the booking just created. */
  overScheduled: number;
}
export interface UpfrontShortfallBookingWarning {
  code: 'PACKAGE_UPFRONT_SHORTFALL';
  packageId: string;
  packageTotal: string;
  earmarkedReceived: string;
  shortfall: string;
}
export type BookingWarning = OverScheduleWarning | UpfrontShortfallBookingWarning;
export type BookingCreated = BookingView & { warnings: BookingWarning[] };

export const listBookings = (spec: QuerySpecInput) =>
  apiRequest<PagedResult<BookingView>>({
    base: 'clinic',
    path: '/v1/bookings',
    query: { query: buildQueryParam(spec) },
  });

/**
 * Walk every page of a bounded window (a calendar view needs all of them).
 * Pages are the backend's own; nothing is invented. Capped at 20 pages ×
 * 100 rows per window for safety.
 */
export async function listBookingsWindow(
  spec: Omit<QuerySpecInput, 'cursor' | 'limit'>,
): Promise<BookingView[]> {
  const out: BookingView[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 20; i += 1) {
    const page = await listBookings({ ...spec, limit: 100, cursor });
    out.push(...page.items);
    if (!page.nextCursor) break;
    cursor = page.nextCursor;
  }
  return out;
}

export const listPatientBookings = (patientId: string) =>
  apiRequest<{ items: BookingView[] }>({
    base: 'clinic',
    path: `/v1/patients/${patientId}/bookings`,
  });

export const fetchBooking = (id: string) =>
  apiRequest<BookingView>({ base: 'clinic', path: `/v1/bookings/${id}` });

export const createBooking = (patientId: string, body: CreateBookingBody) =>
  apiRequest<BookingCreated>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/bookings`,
    body,
  });

export const rescheduleBooking = (id: string, body: RescheduleBookingBody) =>
  apiRequest<BookingView>({ base: 'clinic', method: 'PATCH', path: `/v1/bookings/${id}`, body });

export const cancelBooking = (id: string, reason?: string) =>
  apiRequest<BookingView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/bookings/${id}/cancel`,
    body: reason ? { reason } : {},
  });

export const noShowBooking = (id: string, reason?: string) =>
  apiRequest<BookingView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/bookings/${id}/no-show`,
    body: reason ? { reason } : {},
  });

/* ------------------------- Smart Scheduling (B6) --------------------------- */

export const STRATEGIES = ['preferred_fit', 'steady_rhythm', 'soonest_completion'] as const;
export type Strategy = (typeof STRATEGIES)[number];

export interface SuggestionPreferences {
  practitionerUid?: string;
  roomId?: string;
  /** ISO weekdays 1=Monday…7=Sunday, clinic-local. */
  weekdays?: number[];
  timeWindows?: { startMinute: number; endMinute: number }[];
}

/** Exactly the CreateBookingDto fields + score/reasons — posts back unchanged. */
export interface Suggestion {
  startsAt: string;
  endsAt: string;
  serviceId: string;
  practitionerUid: string;
  roomId: string | null;
  locationType: LocationType;
  travelAllowanceMinutes: number;
  packageItemId: string | null;
  score: number;
  reasons: string[];
  tradeoffs: string[];
}

export interface SuggestSessionsResult {
  serviceId: string;
  locationType: LocationType;
  durationMinutes: number;
  horizonDays: number;
  strategies: { strategy: Strategy; suggestions: Suggestion[] }[];
}

export interface PlanStrategy {
  strategy: Strategy;
  complete: boolean;
  sessions: Suggestion[];
  tradeoffs: string[];
}

export interface SuggestPlanResult {
  packageItemId: string;
  serviceId: string;
  remainingSessions: number;
  durationMinutes?: number;
  horizonDays?: number;
  strategies: PlanStrategy[];
}

export const suggestSessions = (
  patientId: string,
  body: {
    serviceId: string;
    locationType?: LocationType;
    packageItemId?: string;
    horizonDays?: number;
    limit?: number;
    preferences?: SuggestionPreferences;
  },
) =>
  apiRequest<SuggestSessionsResult>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/scheduling/suggestions`,
    body,
  });

export const suggestPlan = (
  patientId: string,
  body: {
    packageItemId: string;
    locationType?: LocationType;
    horizonDays?: number;
    preferences?: SuggestionPreferences;
  },
) =>
  apiRequest<SuggestPlanResult>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/scheduling/plans`,
    body,
  });

/* ───────────── Smart Scheduling v2: whole-package plans (ZOY-682) ───────────── */

export const DAY_PATTERNS = ['fastest', 'weekdays'] as const;
export type DayPattern = (typeof DAY_PATTERNS)[number];
export const THERAPIST_MODES = ['any', 'usual', 'specific'] as const;
export type TherapistMode = (typeof THERAPIST_MODES)[number];
export const PLAN_MODELS = ['soonest_completion', 'steady_time', 'best_fit'] as const;
export type PlanModel = (typeof PLAN_MODELS)[number];
/** The backend's per-request ceiling on `plans/apply`; a longer plan applies in batches. */
export const APPLY_PLAN_MAX_SESSIONS = 30;

export interface PlanSession extends Suggestion {
  /** Clinic-local calendar day, `YYYY-MM-DD` — the visit day this session belongs to. */
  day: string;
}
export interface VisitDay {
  day: string;
  sessions: number;
  /** Two services placed back to back on this day. */
  paired: boolean;
}
export interface PackagePlan {
  model: PlanModel;
  complete: boolean;
  sessions: PlanSession[];
  visitDays: VisitDay[];
  tradeoffs: string[];
}
export interface PackagePlanLine {
  packageItemId: string;
  serviceId: string;
  remaining: number;
  durationMinutes: number | null;
  locationType: LocationType | null;
}
export interface SuggestPackagePlansResult {
  packageId: string;
  dayPattern: DayPattern;
  weekdays: number[] | null;
  therapistMode: TherapistMode;
  therapists: { serviceId: string; practitionerUid: string }[];
  remainingSessions: number;
  horizonDays: number;
  horizonDaysCap: number;
  lines: PackagePlanLine[];
  /** Up to three distinct plans inside the chosen options; empty with `unavailableReason` under `specific`. */
  plans: PackagePlan[];
  unavailableReason: string | null;
}

export const suggestPackagePlans = (
  patientId: string,
  body: {
    packageId: string;
    dayPattern: DayPattern;
    weekdays?: number[];
    therapistMode: TherapistMode;
    therapists?: { serviceId: string; practitionerUid: string }[];
    locationType?: LocationType;
    timeWindows?: { startMinute: number; endMinute: number }[];
  },
) =>
  apiRequest<SuggestPackagePlansResult>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/scheduling/package-plans`,
    body,
  });

/** All-or-nothing: one transaction; a stale slot fails the whole batch with a typed 409. */
export const applyPlan = (patientId: string, sessions: CreateBookingBody[]) =>
  apiRequest<{ bookings: BookingCreated[] }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/scheduling/plans/apply`,
    body: { sessions },
  });

/* ─────────────────── clinical workflow resolution (ZOY-543) ─────────────── */

export interface WorkflowDescriptor {
  type: string;
  /** 'assessment' today; null = the path is plain delivery. */
  artifact: 'assessment' | null;
  /** ZOY-548: completing the session requires its own signed artifact first. */
  requiresArtifactCompletion: boolean;
}

export interface BookingWorkflow {
  bookingId: string;
  serviceId: string;
  /** null = unclassified legacy service — today's behaviour, never an error. */
  workflowType: string | null;
  workflow: WorkflowDescriptor | null;
}

/** Which clinical path does this booked session belong to — computed, never stored. */
export const fetchBookingWorkflow = (bookingId: string) =>
  apiRequest<BookingWorkflow>({ base: 'clinic', path: `/v1/bookings/${bookingId}/workflow` });
